import { useState } from 'react';
import { api } from '@/api/client';
import { PageTitle } from '@/components/ui';

// Deep-link targets a notification tap can open in the game client. Mirrors the
// `data.screen` values the client whitelists in pushService.ts.
const SCREENS = ['home', 'shop', 'inventory', 'leaderboard', 'friends', 'settings'] as const;

// Language targeting for broadcast. '' = all devices. Codes match the server's
// SUPPORTED_LOCALES (DeviceToken.locale).
const LANGS = [
  { code: '', label: 'All languages' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'en', label: 'English' },
  { code: 'zh', label: '中文' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'th', label: 'ไทย' },
] as const;

export function PushPage() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [screen, setScreen] = useState<(typeof SCREENS)[number]>('home');
  const [lang, setLang] = useState<(typeof LANGS)[number]['code']>('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const valid = title.trim().length > 0 && body.trim().length > 0;

  async function send(mode: 'test' | 'broadcast') {
    if (!valid || busy) return;
    const langLabel = LANGS.find((l) => l.code === lang)?.label ?? 'All languages';
    if (
      mode === 'broadcast' &&
      !confirm(`Send this push to ALL ${lang ? `${langLabel} ` : ''}devices?`)
    )
      return;
    setBusy(true);
    setResult(null);
    const input = { title: title.trim(), body: body.trim(), data: { screen } };
    try {
      const r =
        mode === 'test'
          ? await api.pushTest(input)
          : await api.pushBroadcast(lang ? { ...input, locale: lang } : input);
      setResult(`✓ ${mode === 'test' ? 'Test' : 'Broadcast'} sent to ${r.sent} device(s).`);
    } catch (e: any) {
      const code = e?.code ?? e?.message ?? 'ERROR';
      setResult(
        code === 'PUSH_NOT_CONFIGURED'
          ? '✗ Push is not configured on the server (FIREBASE_SERVICE_ACCOUNT missing).'
          : `✗ ${code}`,
      );
    }
    setBusy(false);
  }

  return (
    <div>
      <PageTitle title="Push Notifications" />
      <div
        className="card"
        style={{
          padding: '10px 14px',
          marginBottom: 16,
          fontSize: 12,
          color: 'var(--ink-muted)',
          background: 'rgba(34,211,238,0.08)',
          borderColor: 'rgba(34,211,238,0.25)',
        }}
      >
        Send a one-off announcement to players' devices (FCM). Use <b>TEST</b> first — it sends only
        to your own logged-in devices — then <b>BROADCAST</b> to everyone. Tapping the notification
        opens the chosen screen in the game.
      </div>

      <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 560 }}>
        <label style={{ fontSize: 12 }}>
          <div className="tag" style={{ marginBottom: 4 }}>TITLE</div>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder="e.g. Cuối tuần nhân đôi vàng! 🪙"
            style={{ width: '100%' }}
          />
        </label>

        <label style={{ fontSize: 12 }}>
          <div className="tag" style={{ marginBottom: 4 }}>BODY</div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="e.g. Đăng nhập ngay để nhận thưởng x2 cuối tuần này!"
            style={{ width: '100%', resize: 'vertical' }}
          />
        </label>

        <label style={{ fontSize: 12 }}>
          <div className="tag" style={{ marginBottom: 4 }}>OPEN SCREEN ON TAP</div>
          <select value={screen} onChange={(e) => setScreen(e.target.value as any)} style={{ width: '100%' }}>
            {SCREENS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>

        <label style={{ fontSize: 12 }}>
          <div className="tag" style={{ marginBottom: 4 }}>TARGET LANGUAGE (broadcast only)</div>
          <select value={lang} onChange={(e) => setLang(e.target.value as any)} style={{ width: '100%' }}>
            {LANGS.map((l) => (
              <option key={l.code || 'all'} value={l.code}>{l.label}</option>
            ))}
          </select>
          <div style={{ marginTop: 4, color: 'var(--ink-muted)' }}>
            Broadcast goes only to devices set to this language. Type your message in that language.
            Test always goes to your own devices.
          </div>
        </label>

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button className="btn" onClick={() => send('test')} disabled={!valid || busy}>
            SEND TEST (me)
          </button>
          <button className="btn btn-primary" onClick={() => send('broadcast')} disabled={!valid || busy}>
            BROADCAST TO ALL
          </button>
        </div>

        {result && (
          <div
            style={{
              fontSize: 12,
              marginTop: 4,
              color: result.startsWith('✓') ? 'var(--c-green, #4ade80)' : 'var(--c-red)',
              fontFamily: 'var(--f-mono)',
            }}
          >
            {result}
          </div>
        )}
      </div>
    </div>
  );
}
